import * as React from "react";
import { Alert, Spin } from "antd";
import ReactECharts from "echarts-for-react";
import type { EChartsOption } from "echarts";
import { ChartDataPoint, CHART_COLORS, CHART_HEIGHT } from "./utils";

export interface BaseChartProps {
  title: string;
  loading: boolean;
  error: string | null;
  chartData: ChartDataPoint[];
  seriesKeys: string[];
  formatYAxis?: (value: number) => string;
  formatTooltip?: (value: number) => string;
  tooltipSuffix?: string;
  colors?: string[];
  getColor?: (key: string, index: number) => string;
  /** Sort order for tooltip items: "desc" (big to small) or "asc" (small to big). Default: "desc" */
  tooltipSortOrder?: "asc" | "desc";
  /** Timezone label to show in tooltip (e.g., "Local" or "UTC") */
  timezoneLabel?: string;
}

const chartContainerStyle: React.CSSProperties = {
  backgroundColor: "var(--color-drop)",
  borderRadius: 6,
  padding: 12,
};

const chartTitleStyle: React.CSSProperties = {
  color: "var(--color-default)",
};

// Helper function - defined outside component to avoid recreation
const getIsDark = () => {
  const pageElement = document.getElementById("page");
  return pageElement?.classList.contains("dark") ?? false;
};

export const BaseChart: React.FC<BaseChartProps> = ({
  title,
  loading,
  error,
  chartData,
  seriesKeys,
  formatYAxis,
  formatTooltip,
  tooltipSuffix = "",
  colors = CHART_COLORS,
  getColor,
  tooltipSortOrder = "desc",
  timezoneLabel = "Local",
}) => {
  // Track which series are visible (empty set means all visible)
  const [visibleSeries, setVisibleSeries] = React.useState<Set<string>>(new Set());
  const chartRef = React.useRef<ReactECharts>(null);

  const [themeColors, setThemeColors] = React.useState(() => {
    const isDark = getIsDark();
    return {
      text: isDark ? "#e5e5e5" : "#666666",
      grid: isDark ? "rgba(210, 210, 210, 0.2)" : "#e0e0e0",
      tooltipBg: isDark ? "#1f1f1f" : "#ffffff",
      tooltipBorder: isDark ? "rgba(210, 210, 210, 0.2)" : "#e0e0e0",
    };
  });

  React.useEffect(() => {
    const updateColors = () => {
      const isDark = getIsDark();

      setThemeColors({
        text: isDark ? "#e5e5e5" : "#666666",
        grid: isDark ? "rgba(210, 210, 210, 0.2)" : "#e0e0e0",
        tooltipBg: isDark ? "#1f1f1f" : "#ffffff",
        tooltipBorder: isDark ? "rgba(210, 210, 210, 0.2)" : "#e0e0e0",
      });
    };

    updateColors();

    // Watch for theme changes on #page element
    const pageElement = document.getElementById("page");
    if (!pageElement) return;

    const observer = new MutationObserver(updateColors);
    observer.observe(pageElement, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, []);

  // Helper functions using React.useCallback to maintain stable references
  const getLineColor = React.useCallback(
    (key: string, index: number) => {
      if (getColor) {
        return getColor(key, index);
      }
      return colors[index % colors.length];
    },
    [getColor, colors]
  );

  // Check if a series is currently visible
  const isSeriesVisible = React.useCallback(
    (key: string) => {
      // If visibleSeries is empty, all series are visible
      if (visibleSeries.size === 0) return true;
      return visibleSeries.has(key);
    },
    [visibleSeries]
  );

  // Handle legend click to filter series
  const handleLegendClick = React.useCallback(
    (dataKey: string) => {
      setVisibleSeries((prev) => {
        // If no filter is active (all visible), clicking an item shows only that item
        if (prev.size === 0) {
          return new Set([dataKey]);
        }

        const next = new Set(prev);

        if (next.has(dataKey)) {
          // Clicking a visible item removes it from the filter
          next.delete(dataKey);
          // If no items left, reset to show all
          if (next.size === 0) {
            return new Set();
          }
        } else {
          // Clicking a hidden item adds it to the visible set
          next.add(dataKey);
          // If all items are now visible, reset to show all (empty set)
          if (next.size === seriesKeys.length) {
            return new Set();
          }
        }

        return next;
      });
    },
    [seriesKeys.length]
  );

  // Build ECharts option
  const echartsOption: EChartsOption = React.useMemo(() => {
    // X-axis categories (time labels)
    const categories = chartData.map((d) => d.formattedTime);

    // Build series for each key
    const series: any[] = seriesKeys.map((key, index) => {
      const color = getLineColor(key, index);
      const visible = isSeriesVisible(key);

      return {
        name: key,
        type: "line",
        data: chartData.map((d) => (d[key] as number) ?? null),
        lineStyle: {
          color: color,
          width: 2,
          opacity: visible ? 1 : 0,
        },
        itemStyle: {
          color: color,
        },
        symbol: "none",
        emphasis: {
          focus: "series",
        },
        // Hide series by setting line width to 0
        ...(visible ? {} : { lineStyle: { width: 0 }, areaStyle: { opacity: 0 } }),
      };
    });

    return {
      grid: {
        left: 10,
        right: 15,
        top: 10,
        bottom: 10,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: categories,
        axisLabel: {
          color: themeColors.text,
          fontSize: 10,
        },
        axisLine: {
          lineStyle: {
            color: themeColors.grid,
          },
        },
        axisTick: {
          show: false,
        },
      },
      yAxis: {
        type: "value",
        axisLabel: {
          formatter: formatYAxis || ((value: number) => `${value}`),
          color: themeColors.text,
          fontSize: 10,
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: themeColors.grid,
          },
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          lineStyle: {
            type: "dashed",
            color: themeColors.grid,
          },
        },
      },
      series,
      tooltip: {
        trigger: "axis",
        backgroundColor: themeColors.tooltipBg,
        borderColor: themeColors.tooltipBorder,
        textStyle: {
          color: themeColors.text,
        },
        confine: true,
        extraCssText: "max-height: 400px; overflow-y: auto;",
        formatter: (params: any) => {
          if (!Array.isArray(params) || params.length === 0) return "";

          const time = params[0].axisValue;

          // Calculate max value to determine significance threshold (1% of max)
          const maxValue = Math.max(...params.map((p: any) => Math.abs(p.value || 0)));
          const significanceThreshold = maxValue * 0.01;

          // Filter out hidden series and insignificant values
          const filteredParams = params.filter((p: any) => {
            if (!isSeriesVisible(p.seriesName)) return false;
            if (p.value === null || p.value === undefined) return false;
            if (Math.abs(p.value || 0) < significanceThreshold) return false;
            return true;
          });

          // Sort by value
          const sortedParams = [...filteredParams].sort((a: any, b: any) => {
            const aVal = a.value || 0;
            const bVal = b.value || 0;
            return tooltipSortOrder === "desc" ? bVal - aVal : aVal - bVal;
          });

          if (sortedParams.length === 0) return "";

          const isDark = getIsDark();
          const rowBgOdd = isDark ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.015)";

          let html = `<div style="padding: 8px 0; min-width: 200px;">`;
          html += `<div style="color: ${themeColors.text}; padding: 0 12px 8px; border-bottom: 1px solid ${themeColors.tooltipBorder};">`;
          html += `${time} (${timezoneLabel})`;
          html += `</div>`;
          html += `<div style="display: table; width: 100%; border-collapse: collapse;">`;

          sortedParams.forEach((entry: any, index: number) => {
            const bgColor = index % 2 === 0 ? "transparent" : rowBgOdd;
            const formattedValue = formatTooltip
              ? formatTooltip(entry.value)
              : `${entry.value.toFixed(2)}${tooltipSuffix}`;

            html += `<div style="display: table-row; background-color: ${bgColor};">`;
            html += `<div style="display: table-cell; padding: 4px 12px; color: ${entry.color}; max-width: 300px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">`;
            html += entry.seriesName;
            html += `</div>`;
            html += `<div style="display: table-cell; padding: 4px 12px; color: ${entry.color}; text-align: right; font-variant-numeric: tabular-nums;">`;
            html += formattedValue;
            html += `</div>`;
            html += `</div>`;
          });

          html += `</div></div>`;
          return html;
        },
      },
      legend: {
        show: false, // We use custom legend below
      },
    };
  }, [chartData, seriesKeys, themeColors, getLineColor, isSeriesVisible, formatYAxis, formatTooltip, tooltipSuffix, tooltipSortOrder, timezoneLabel]);

  // Early returns after all hooks
  if (loading) {
    return (
      <div style={chartContainerStyle}>
        <h2 className="text-base font-medium mb-2" style={chartTitleStyle}>
          {title}
        </h2>
        <div className="flex justify-center items-center" style={{ height: CHART_HEIGHT }}>
          <Spin size="default" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={chartContainerStyle}>
        <h2 className="text-base font-medium mb-2" style={chartTitleStyle}>
          {title}
        </h2>
        <Alert type="error" message="Error" description={error} showIcon style={{ fontSize: 12 }} />
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div style={chartContainerStyle}>
        <h2 className="text-base font-medium mb-2" style={chartTitleStyle}>
          {title}
        </h2>
        <Alert
          type="info"
          message="No data"
          description="No metrics data returned."
          showIcon
          style={{ fontSize: 12 }}
        />
      </div>
    );
  }

  return (
    <div style={chartContainerStyle}>
      <h2 className="text-base font-medium mb-2" style={chartTitleStyle}>
        {title}
      </h2>
      <ReactECharts
        ref={chartRef}
        option={echartsOption}
        style={{ height: CHART_HEIGHT, width: "100%" }}
        notMerge={true}
      />
      {/* Custom legend with max 2 rows and vertical scroll */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: "4px 8px",
          maxHeight: 56,
          overflowY: "auto",
          padding: "4px 0",
          marginTop: 4,
        }}
      >
        {seriesKeys.map((key, index) => {
          const isVisible = isSeriesVisible(key);
          const color = getLineColor(key, index);
          return (
            <div
              key={key}
              onClick={() => handleLegendClick(key)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 3,
                cursor: "pointer",
                opacity: isVisible ? 1 : 0.5,
              }}
            >
              <div
                style={{
                  width: 10,
                  height: 2,
                  backgroundColor: isVisible ? color : themeColors.text,
                }}
              />
              <span
                style={{
                  fontSize: 11,
                  color: isVisible ? color : themeColors.text,
                  textDecoration: isVisible ? "none" : "line-through",
                }}
              >
                {key}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
